import React, { useState, useEffect } from 'react';
import './ProductRating.css';

const ProductRating = ({ product, userRating, onRate }) => {
  const [hoveredStar, setHoveredStar] = useState(0);
  const [showThankYou, setShowThankYou] = useState(false);
  const [animatingStars, setAnimatingStars] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);

  const totalRatings = product.total_ratings || 0;
  const averageRating = parseFloat(product.average_rating) || 0;

  // Get individual star counts
  const starCounts = {
    5: product['5_star'] || 0,
    4: product['4_star'] || 0,
    3: product['3_star'] || 0,
    2: product['2_star'] || 0,
    1: product['1_star'] || 0
  };

  const handleStarClick = (rating) => {
    // Trigger star animation
    setAnimatingStars(true);
    setRatingSubmitted(true);
    
    // Call the rating function
    onRate(rating);
    
    // Show thank you message after animation
    setTimeout(() => {
      setShowThankYou(true);
      setAnimatingStars(false);
    }, 800);
    
    // Hide thank you message after 3 seconds
    setTimeout(() => {
      setShowThankYou(false);
    }, 4000);
  };

  // Reset states when userRating changes (for different products)
  useEffect(() => {
    setRatingSubmitted(false);
    setShowThankYou(false);
    setAnimatingStars(false);
  }, [product.id]);

  const handleStarHover = (rating) => {
    setHoveredStar(rating);
  };

  const handleStarLeave = () => {
    setHoveredStar(0);
  };

  const getStarPercentage = (starCount) => {
    if (totalRatings === 0) return 0;
    return (starCount / totalRatings) * 100;
  };

  return (
    <div className="product-rating-section">
      <h3>Ratings & Reviews</h3>
      
      <div className="rating-overview">
        {/* Average Rating Display */}
        <div className="average-rating-display">
          <div className="average-number">{averageRating.toFixed(1)}</div>
          <div className="average-stars">
            {[1, 2, 3, 4, 5].map(star => {
              const isFilled = star <= averageRating;
              const isPartial = star - 0.5 <= averageRating && star > averageRating;
              
              return (
                <div key={star} className="star-container">
                  <i className="fas fa-star star-bg"></i>
                  {isPartial ? (
                    <i className="fas fa-star-half-alt star-fill"></i>
                  ) : isFilled ? (
                    <i className="fas fa-star star-fill"></i>
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className="total-ratings">{totalRatings} {totalRatings === 1 ? 'rating' : 'ratings'}</div>
        </div>

        {/* Rating Breakdown */}
        <div className="rating-breakdown">
          {[5, 4, 3, 2, 1].map(star => (
            <div key={star} className="rating-row">
              <span className="star-label">{star}</span>
              <i className="fas fa-star star-icon"></i>
              <div className="rating-bar">
                <div 
                  className="rating-fill"
                  style={{ width: `${getStarPercentage(starCounts[star])}%` }}
                ></div>
              </div>
              <span className="rating-count">{starCounts[star]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* User Rating Section */}
      <div className="user-rating-section">
        <h4>Rate this product</h4>
        
        {/* Thank You Message */}
        {showThankYou && (
          <div className="thank-you-message">
            <div className={`thank-you-content rating-${userRating}`}>
              <i className={`fas ${
                userRating === 5 ? 'fa-heart' :
                userRating === 4 ? 'fa-thumbs-up' :
                userRating === 3 ? 'fa-smile' :
                userRating === 2 ? 'fa-meh' : 'fa-frown'
              }`}></i>
              <span>{
                userRating === 5 ? 'Awesome! Thanks for the 5 stars!' :
                userRating === 4 ? 'Great! Thanks for the rating!' :
                userRating === 3 ? 'Thanks for your feedback!' :
                userRating === 2 ? 'Thanks for the honest feedback!' :
                'Thanks for letting us know!'
              }</span>
              <div className="rating-sparkles">
                <i className="fas fa-sparkles"></i>
                <i className="fas fa-sparkles"></i>
                <i className="fas fa-sparkles"></i>
              </div>
            </div>
          </div>
        )}
        
        <div className={`rate-now-container ${animatingStars ? 'animating' : ''}`}>
          {[1, 2, 3, 4, 5].map(star => {
            const isActive = hoveredStar > 0 ? star <= hoveredStar : star <= userRating;
            
            return (
              <button
                key={star}
                className={`star-button ${isActive ? 'active' : ''} ${
                  animatingStars && star <= userRating ? 'bounce' : ''
                }`}
                onClick={() => handleStarClick(star)}
                onMouseEnter={() => handleStarHover(star)}
                onMouseLeave={handleStarLeave}
                title={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                disabled={animatingStars}
                style={{ animationDelay: `${(star - 1) * 0.1}s` }}
              >
                <i className="fas fa-star"></i>
                {animatingStars && star <= userRating && (
                  <div className="star-particles">
                    <span className="particle"></span>
                    <span className="particle"></span>
                    <span className="particle"></span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="rating-labels">
          <span className="rating-text">
            {hoveredStar > 0 ? (
              hoveredStar === 1 ? 'Poor' :
              hoveredStar === 2 ? 'Fair' :
              hoveredStar === 3 ? 'Good' :
              hoveredStar === 4 ? 'Very Good' : 'Excellent'
            ) : userRating > 0 ? (
              userRating === 1 ? 'Poor' :
              userRating === 2 ? 'Fair' :
              userRating === 3 ? 'Good' :
              userRating === 4 ? 'Very Good' : 'Excellent'
            ) : 'Tap to rate'}
          </span>
        </div>
        {userRating > 0 && (
          <p className="user-rating-text">
            You rated this product {userRating} star{userRating !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  );
};

export default ProductRating;
